import type { GameState, Move, BoardEvent, Player } from './types'
import { applyMoveToState } from './moves'
import { getLegalMoves, isInCheck, evaluateEliminations, evaluateWinCondition, advancePastEliminated } from './rules'
import { resolvePendingMoves } from './simultaneous'
import { getBotMove } from './bot'
import { processEventQueue } from './events'
import { spawnPickup, spawnPickupBalanced } from './Board'

type EventMap = {
  stateChange: [prev: GameState, next: GameState]
  boardEventFired: [event: BoardEvent, next: GameState]
  gameOver: [winner: Player | null, finalState: GameState]
  checkAlert: [player: Player, state: GameState]
}

type Listener<T extends unknown[]> = (...args: T) => void

export type GameOptions = {
  /** Delay in ms before a bot fires its move. Default: 800. Pass 0 in tests. */
  botDelayMs?: number
}

/**
 * Game is the stateful orchestrator. It holds the current GameState and
 * provides methods for the UI to interact with the game. All chess logic
 * lives in pure functions in core/ — Game just wires them together.
 */
export class Game {
  private state: GameState
  private listeners: { [K in keyof EventMap]?: Array<Listener<EventMap[K]>> } = {}
  private history: GameState[] = []  // snapshots for undo (sequential mode only)
  private windowTimer: ReturnType<typeof setTimeout> | null = null  // setTimeout handle for instantaneous window resolution
  private botDelayMs: number

  constructor(initialState: GameState, options: GameOptions = {}) {
    this.state = initialState
    this.botDelayMs = options.botDelayMs ?? 800

    // Simultaneous modes: schedule pending bot submissions when game starts
    if (initialState.phase === 'active') {
      this.schedulePendingBotMoves(initialState)
    }
  }

  // ── Pickup replenishment ────────────────────────────────────────────────────

  /**
   * Remove pickups that have been on the board for pickupLifetimeTurns without being collected.
   */
  private expirePickups(state: GameState): GameState {
    const lifetime = state.pickupLifetimeTurns
    const expired = Object.entries(state.pickups).filter(
      ([, p]) => state.turnNumber - p.spawnedOnTurn >= lifetime
    )
    if (expired.length === 0) return state
    const newPickups = { ...state.pickups }
    for (const [key] of expired) delete newPickups[key]
    return { ...state, pickups: newPickups }
  }

  /**
   * Ensure the board always has exactly `players.length` pickups.
   * Called after every move resolution (sequential or simultaneous).
   */
  private replenishPickups(state: GameState): GameState {
    const target = state.players.length
    const spawn = (state.pickupPlacement ?? 'balanced') === 'balanced' ? spawnPickupBalanced : spawnPickup
    let s = state
    while (Object.keys(s.pickups).length < target) {
      s = spawn(s)
    }
    return s
  }

  // ── Event processing ────────────────────────────────────────────────────────

  /**
   * Process the event queue if the turn number advanced.
   * Applies any transforms, emits boardEventFired for each fired event,
   * and returns the updated state.
   */
  private processEvents(prev: GameState, next: GameState): GameState {
    if (next.eventQueue.length === 0) return next
    if (next.turnNumber === prev.turnNumber) return next   // no turn advance — no event check

    const [newState, fired] = processEventQueue(next)
    for (const event of fired) {
      this.emit('boardEventFired', event, newState)
    }
    return newState
  }

  // ── Bot scheduling ─────────────────────────────────────────────────────────

  /**
   * If the current active player in sequential mode is a bot, schedule its move.
   * Called after every applyMove so bots chain automatically.
   */
  private scheduleNextBotTurn(state: GameState): void {
    if (state.mode !== 'sequential') return
    if (state.phase !== 'active') return

    const player = state.players[state.activePlayer]
    if (!player || !player.alive || player.controlledBy !== 'bot') return

    setTimeout(() => {
      // Re-check state hasn't changed (game over, etc.)
      if (this.state.phase !== 'active') return
      if (this.state.activePlayer !== player.index) return

      const move = getBotMove(this.state, player.index)
      if (move !== null) {
        this.applyMove(move)
      }
    }, this.botDelayMs)
  }

  /**
   * In simultaneous modes, schedule pending move submissions for all bot players.
   * Called once at game start and after each round resolves.
   */
  private schedulePendingBotMoves(state: GameState): void {
    if (state.mode === 'sequential') return
    if (state.phase !== 'active') return

    const aliveBots = state.players.filter(p => p.alive && p.controlledBy === 'bot')
    if (aliveBots.length === 0) return

    for (const bot of aliveBots) {
      setTimeout(() => {
        if (this.state.phase !== 'active') return
        // Don't double-submit if already pending
        if (this.state.pendingMoves.some(m => m.player === bot.index)) return

        const move = getBotMove(this.state, bot.index)
        if (move !== null) {
          if (this.state.mode === 'simultaneous_resolution') {
            this.submitPendingMove(move)
          } else {
            // simultaneous_instantaneous: bot fires into the window
            this.applyMove(move)
          }
        }
      }, this.botDelayMs)
    }
  }

  // ── Event emitter ──────────────────────────────────────────────────────────

  on<K extends keyof EventMap>(event: K, listener: Listener<EventMap[K]>): void {
    if (!this.listeners[event]) {
      this.listeners[event] = []
    }
    (this.listeners[event] as Array<Listener<EventMap[K]>>).push(listener)
  }

  off<K extends keyof EventMap>(event: K, listener: Listener<EventMap[K]>): void {
    const arr = this.listeners[event] as Array<Listener<EventMap[K]>> | undefined
    if (!arr) return
    const idx = arr.indexOf(listener)
    if (idx !== -1) arr.splice(idx, 1)
  }

  private emit<K extends keyof EventMap>(event: K, ...args: EventMap[K]): void {
    const arr = this.listeners[event] as Array<Listener<EventMap[K]>> | undefined
    if (!arr) return
    for (const listener of arr) {
      listener(...args)
    }
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  getState(): GameState {
    return this.state
  }

  /**
   * Sequential mode only: restore the state before the last move was applied.
   * Returns true if undo succeeded, false if nothing to undo.
   */
  undoLastMove(): boolean {
    if (this.state.mode !== 'sequential' || this.history.length === 0) return false
    const prev = this.state
    const restored = this.history.pop()!
    this.state = restored
    this.emit('stateChange', prev, restored)
    return true
  }

  canUndo(): boolean {
    return this.state.mode === 'sequential' && this.history.length > 0
  }

  /**
   * Online mode: replace internal state with a server-provided state and emit events.
   * Called when a 'joined' or 'state_sync' message arrives from the server.
   */
  loadState(newState: GameState): void {
    const prev = this.state
    this.state = newState
    this.emit('stateChange', prev, newState)
    for (const p of newState.players) {
      if (p.alive && p.inCheck) this.emit('checkAlert', p, newState)
    }
    if (newState.phase === 'gameover') {
      const winner = newState.winner !== null ? newState.players[newState.winner] : null
      this.emit('gameOver', winner, newState)
    }
  }

  /**
   * Apply a move. Validates that the move is in the legal move list before
   * applying. Emits 'stateChange' after applying.
   */
  applyMove(move: Move): void {
    const { from, to, player } = move

    // Only the active player can move in sequential mode
    if (this.state.mode === 'sequential' && player !== this.state.activePlayer) {
      console.warn(`Not player ${player}'s turn`)
      return
    }

    // In instantaneous mode: use commit window logic instead of immediate application
    if (this.state.mode === 'simultaneous_instantaneous') {
      this.handleInstantaneousMove(move)
      return
    }

    // Validate legality
    const legalDests = getLegalMoves(this.state, from)
    if (!legalDests.includes(to)) {
      console.warn(`Illegal move: ${from} → ${to}`)
      return
    }

    const prev = this.state
    if (prev.mode === 'sequential') this.history.push(prev)
    let next = applyMoveToState(prev, move)

    // Update inCheck status for all alive players
    next = {
      ...next,
      players: next.players.map(p => ({
        ...p,
        inCheck: p.alive ? isInCheck(next, p.index) : false,
      })),
    }

    // Eliminate players whose king was captured or who are in checkmate
    next = evaluateEliminations(next)

    // Advance past any newly-eliminated active player
    next = advancePastEliminated(next)

    // Evaluate win condition (may set phase='gameover')
    next = evaluateWinCondition(next, move.player)

    // Fire board events if the turn number advanced
    next = this.processEvents(prev, next)

    // Expire stale pickups, then replenish to target count
    next = this.expirePickups(next)
    next = this.replenishPickups(next)

    this.state = next
    this.emit('stateChange', prev, next)

    // Emit check alerts for surviving players
    for (const p of next.players) {
      if (p.alive && p.inCheck) this.emit('checkAlert', p, next)
    }

    // Emit game over if applicable
    if (next.phase === 'gameover') {
      const winner = next.winner !== null ? next.players[next.winner] : null
      this.emit('gameOver', winner, next)
    }

    // Schedule next bot turn if applicable (sequential mode)
    this.scheduleNextBotTurn(next)
  }

  /**
   * Handle move in simultaneous_instantaneous mode with 5-second commit window.
   * Buffers the move and opens/manages the commit window.
   */
  private handleInstantaneousMove(move: Move): void {
    const { from, to, player } = move

    // Reject if player already has a pending move this window
    const existingPendingIndex = this.state.pendingMoves.findIndex(m => m.player === player)
    if (existingPendingIndex !== -1) {
      console.warn(`Player ${player} already submitted a move this window`)
      return
    }

    // Validate legality
    const legalDests = getLegalMoves(this.state, from)
    if (!legalDests.includes(to)) {
      console.warn(`Illegal move: ${from} → ${to}`)
      return
    }

    const prev = this.state
    const pending = [...prev.pendingMoves, move]

    // Open window on first move of this round
    let windowOpenAt = prev.windowOpenAt
    if (windowOpenAt === null) {
      windowOpenAt = Date.now()

      // Schedule window resolution
      this.windowTimer = setTimeout(() => {
        this.doResolveInstantaneousWindow()
      }, prev.instantaneousWindowMs)
    }

    const next = { ...prev, pendingMoves: pending, windowOpenAt }
    this.state = next
    this.emit('stateChange', prev, next)

    // Check if all alive players have now submitted — if so, resolve early
    const alivePlayers = next.players.filter(p => p.alive)
    const pendingSet = new Set(next.pendingMoves.map(m => m.player))
    if (alivePlayers.every(p => pendingSet.has(p.index))) {
      if (this.windowTimer !== null) {
        clearTimeout(this.windowTimer)
        this.windowTimer = null
      }
      this.doResolveInstantaneousWindow()
    }
  }

  /**
   * Manually trigger resolution of the instantaneous commit window (for testing).
   */
  resolveInstantaneousWindow(): void {
    if (this.state.mode !== 'simultaneous_instantaneous' || this.state.windowOpenAt === null) {
      return
    }

    if (this.windowTimer !== null) {
      clearTimeout(this.windowTimer)
      this.windowTimer = null
    }

    this.doResolveInstantaneousWindow()
  }

  /**
   * Internal method to resolve the instantaneous commit window.
   */
  private doResolveInstantaneousWindow(): void {
    const prev = this.state

    // Clear the timer and window state
    this.windowTimer = null
    let next: GameState = { ...prev, windowOpenAt: null }

    // Use simultaneous resolution logic to resolve pending moves
    next = resolvePendingMoves(next)

    // Update check status for all alive players
    next = {
      ...next,
      players: next.players.map(p => ({
        ...p,
        inCheck: p.alive ? isInCheck(next, p.index) : false,
      })),
    }

    // Eliminate players and advance game state
    next = evaluateEliminations(next)
    next = advancePastEliminated(next)
    next = evaluateWinCondition(next, -1) // No specific triggering player for simultaneous resolution

    // Fire board events if the turn number advanced
    next = this.processEvents(prev, next)

    // Expire stale pickups, then replenish to target count
    next = this.expirePickups(next)
    next = this.replenishPickups(next)

    this.state = next
    this.emit('stateChange', prev, next)

    // Emit check alerts and game over events
    for (const p of next.players) {
      if (p.alive && p.inCheck) this.emit('checkAlert', p, next)
    }

    if (next.phase === 'gameover') {
      const winner = next.winner !== null ? next.players[next.winner] : null
      this.emit('gameOver', winner, next)
    }
  }

  /**
   * Simultaneous resolution mode: buffer a pending move for a player.
   * Replaces any previous pending move from that player this round.
   * Does not validate legality here — validation happens at resolveRound.
   */
  submitPendingMove(move: Move): void {
    const prev = this.state
    const pending = prev.pendingMoves.filter(m => m.player !== move.player)
    pending.push(move)
    const next = { ...prev, pendingMoves: pending }
    this.state = next
    this.emit('stateChange', prev, next)

    // Auto-resolve if all alive players have now submitted a pending move
    const alivePlayers = next.players.filter(p => p.alive)
    const pendingSet = new Set(next.pendingMoves.map(m => m.player))
    if (alivePlayers.every(p => pendingSet.has(p.index))) {
      this.resolveRound()
    }
  }

  /**
   * Simultaneous resolution mode: resolve all pending moves via conflict resolution,
   * then run the full post-move pipeline (check, elimination, win condition).
   */
  resolveRound(): void {
    const prev = this.state

    // Resolve conflicts and apply moves
    let next = resolvePendingMoves(prev)

    // Update inCheck for all alive players
    next = {
      ...next,
      players: next.players.map(p => ({
        ...p,
        inCheck: p.alive ? isInCheck(next, p.index) : false,
      })),
    }

    // Eliminate players in checkmate or whose king was captured
    next = evaluateEliminations(next)
    next = advancePastEliminated(next)

    // In simultaneous_resolution mode the "capturing player" is ambiguous; pass -1 so
    // first_blood falls back to evaluating the surviving player as winner.
    next = evaluateWinCondition(next, -1)

    // Fire board events if the turn number advanced
    next = this.processEvents(prev, next)

    // Expire stale pickups, then replenish to target count
    next = this.expirePickups(next)
    next = this.replenishPickups(next)

    this.state = next
    this.emit('stateChange', prev, next)

    for (const p of next.players) {
      if (p.alive && p.inCheck) this.emit('checkAlert', p, next)
    }

    if (next.phase === 'gameover') {
      const winner = next.winner !== null ? next.players[next.winner] : null
      this.emit('gameOver', winner, next)
    }

    // Re-schedule bot pending moves for the next resolution round
    this.schedulePendingBotMoves(next)
  }

}
